import React, {useEffect, useState} from 'react';
import { useTranslation } from 'react-i18next';
import { LinearProgress, styled, Box, Typography } from '@mui/material';

import useDataSource from '../../../hooks/useDataSource';

// Custom Components
import SmartButton from './SmartButton'

// Custom Utilities
import {
    getFieldFormatters
} from '../../../utilities/DataSourceUtilities';

import {
    formatValue,
    formatTypographyColor
} from '../../../utilities/FormatUtilities';

const PREFIX = 'CallToAction';

const classes = {
    root: `${PREFIX}-root`,
    linearProgress: `${PREFIX}-linearProgress`,
    container: `${PREFIX}-container`,
    buttonContainer: `${PREFIX}-buttonContainer`
};

const StyledBox = styled(Box)((
    {
        theme
    }
) => ({
    [`&.${classes.root}`]: {
        height: '100%',
		backgroundColor: theme.palette.background.default
    },

    [`& .${classes.linearProgress}`]: {
        height: '1px'
    },

    [`& .${classes.container}`]: {
        display: 'flex',
        height: '100%',
        overflowY: 'auto',
        flexDirection: 'column'
    },

    [`& .${classes.buttonContainer}`]: {
        paddingTop: '10px'
    }
}));

export default function CallToAction({id, configuration, onLoading, onLoaded}) {


    const { t, i18n } = useTranslation();

    // https://jacobsengineering.sharepoint.com/sites/CPOV012053/Lists/Home/Home.aspx

    //     configuration: {
    //          dataSource: {
    //              name: <datasource>
    //              fields: {
    //                  headline: <headline field name from datasource>
    //                  subheadline: <subheadline field name from datasource>
    //                  link: <information field name from datasource>
    //              }
    //          },
    //          element: {
    //              headline: {
    //                  color: initial | inherit | primary | secondary | textPrimary | textSecondary | error | HEX code
    //                  variant: h1 | h2 | h3 | h4 | h5 | h6 | subtitle1 | subtitle2 | body1 | body2 | caption | button | overline | srOnly | inherit
    //                  justifyContent: flex-start | center | flex-end
    //                  textAlign: left | center | right
    //              },
    //              subheadline: {
    //                  color: initial | inherit | primary | secondary | textPrimary | textSecondary | error | HEX code
    //                  variant: h1 | h2 | h3 | h4 | h5 | h6 | subtitle1 | subtitle2 | body1 | body2 | caption | button | overline | srOnly | inherit
    //                  justifyContent: flex-start | center | flex-end
    //                  textAlign: left | center | right
    //              },
    //              button: {
    //                  color: default | inherit | primary | secondary
    //                  label: <button label>
    //                  size: large | medium | small
    //                  variant: contained | outlined | text
    //              }
    //              buttonLink: {
    //                  newWindow: true | false
    //                  protocol: file | ftp | mailto | tel !!not using; getting from datasource formatter!!
    //              }
    //              buttonPosition: {
    //                  alignItems: stretch | center | flex-start | flex-end | baseline | initial | inherit
    //                  justifyContent: flex-start | flex-end | center | space-between | space-around | initial | inherit
    //              }
    //          }
    //      }

    // DATASOURCE
    // -- DATASOURCE: Name
    const dataSourceName = configuration?.dataSource?.name ? configuration?.dataSource?.name : '';
    // -- DATASOURCE: Fields
    const headlineField = configuration?.dataSource?.fields?.headline ? configuration?.dataSource?.fields?.headline : '';
    const subheadlineField = configuration?.dataSource?.fields?.subheadline ? configuration?.dataSource?.fields?.subheadline : '';
    const linkField = configuration?.dataSource?.fields?.link ? configuration?.dataSource?.fields?.link : '';
    // ELEMENT
    // -- ELEMENT: Headline
    const headlineColor = configuration?.element?.headline?.color ? configuration?.element?.headline?.color : 'textPrimary';
    const headlineVariant = configuration?.element?.headline?.variant ? configuration?.element?.headline?.variant : 'h3';
    const headlineJustifyContent = configuration?.element?.headline?.justifyContent ? configuration?.element?.headline?.justifyContent : 'flex-start';
    const headlineTextAlign = configuration?.element?.headline?.textAlign ? configuration?.element?.headline?.textAlign : 'left';
    // -- ELEMENT: Subheadline
    const subheadlineColor = configuration?.element?.subheadline?.color ? configuration?.element?.subheadline?.color : 'textSecondary';
    const subheadlineVariant = configuration?.element?.subheadline?.variant ? configuration?.element?.subheadline?.variant : 'h6';
    const subheadlineJustifyContent = configuration?.element?.subheadline?.justifyContent ? configuration?.element?.subheadline?.justifyContent : 'flex-start';
    const subheadlineTextAlign = configuration?.element?.subheadline?.textAlign ? configuration?.element?.subheadline?.textAlign : 'left';
    // -- ELEMENT: Button
    const buttonColor = configuration?.element?.button?.color ? configuration?.element?.button?.color : 'primary';
    const buttonLabel = configuration?.element?.button?.label ? configuration?.element?.button?.label : 'Button';
    const buttonSize = configuration?.element?.button?.size ? configuration?.element?.button?.size : 'large';
    const buttonVariant = configuration?.element?.button?.variant ? configuration?.element?.button?.variant : 'contained';
    // -- ELEMENT: Button Link
    const buttonLinkTarget = configuration?.element?.buttonLink?.newWindow ? '_blank' : '_blank';
    const buttonLinkProtocol = configuration?.element?.buttonLink?.protocol ? configuration?.element?.buttonLink?.protocol : '';
    // -- ELEMENT: Button Position
    const buttonPositionAlignItems = configuration?.element?.buttonPosition?.alignItems ? configuration?.element?.buttonPosition?.alignItems : 'center';
    const buttonPositionJustifyContent = configuration?.element?.buttonPosition?.justifyContent ? configuration?.element?.buttonPosition?.justifyContent : 'center';
    // -- ELEMENT: Styles
    const styles = configuration?.element?.styles;

    // Button configuration
    const buttonConfiguration = {
        dataSource: {
            name: dataSourceName,
            fields: {
                value: linkField
            }
        },
        element: {
            button: {
                color: buttonColor,
                label: buttonLabel,
                size: buttonSize,
                variant: buttonVariant,
            },
            buttonLink: {
                newWindow: buttonLinkTarget,
                protocol: buttonLinkProtocol
            },
            buttonPosition: {
                alignItems: buttonPositionAlignItems,
                justifyContent: buttonPositionJustifyContent
            }
        }
    };

    // App State
    const dataSource = useDataSource(dataSourceName);

    // Component State
    const [headline, setHeadline] = useState(null);
    const [headlineTypographyColor, setHeadlineTypographyColor] = useState({});
    const [subheadline, setSubheadline] = useState(null);
    const [subheadlineTypographyColor, setSubheadlineTypographyColor] = useState({});
    const [link, setLink] = useState(null);
    const [showProgress, setShowProgress] = useState(isFunction(onLoading) ? false : true);

    // Loading
    useEffect(() => {
        if (dataSource.fetching === true) {
            if (isFunction(onLoading)) {
                onLoading();
            }
        }
    }, [dataSource.fetching]);

    // Loaded
    useEffect(() => {
        if (dataSource.fetching === false && dataSource.fetched === true) {
            if (isFunction(onLoaded)) {
                onLoaded();
            }
        }
    }, [dataSource.fetched]);

    // Data
    useEffect(() => {

        if (dataSource.error) {
            // console.log('-----> CallToAction:useEffect(): dataSource -- ERROR');
            // TODO: Error component

        } else if (dataSource.data && dataSource.fetched) {
            // console.log('-----> CallToAction:useEffect(): dataSource -- FETCHED DATA');

            // Get formatters from datasource
            const headlineFormatters = getFieldFormatters(dataSource, headlineField);
            const subheadlineFormatters = getFieldFormatters(dataSource, subheadlineField);
            const linkFormatters = getFieldFormatters(dataSource, linkField);

            // Set component state
            setHeadline(formatValue(dataSource.data[0][headlineField], headlineFormatters, null, false));
            setHeadlineTypographyColor(formatTypographyColor(headlineColor));
            setSubheadline(formatValue(dataSource.data[0][subheadlineField], subheadlineFormatters, null, false));
            setSubheadlineTypographyColor(formatTypographyColor(subheadlineColor));
            setLink(formatValue(dataSource.data[0][linkField], linkFormatters, null, false));
        } else {
            // console.log('-----> CallToAction:useEffect(): dataSource -- OTHER');

            // Set component state
            setHeadline(null);
            setSubheadline(null);
            setLink(null);
            // TODO: Error component
        }

    }, [dataSource.data]);

    function isFunction(functionToCheck) {
        return functionToCheck && {}.toString.call(functionToCheck) === '[object Function]';
    }

    return (
        <StyledBox className={classes.root} style={{...styles?.box}}>
            {showProgress && dataSource.fetching &&
            <LinearProgress className={classes.linearProgress}/>
            }
            {showProgress && !dataSource.fetching &&
            <div className={classes.linearProgress}></div>
            }
            <Box className={classes.container}>
                {headline &&
                <Box style={{justifyContent: headlineJustifyContent, textAlign: headlineTextAlign}}>
                    <Typography color={headlineTypographyColor.component} variant={headlineVariant} style={{color: headlineTypographyColor.style}}>
                        {t(headline)}
                    </Typography>
                </Box>
                }
                {subheadline &&
                <Box style={{justifyContent: subheadlineJustifyContent, textAlign: subheadlineTextAlign}}>
                    <Typography color={subheadlineTypographyColor.component} variant={subheadlineVariant} style={{color: subheadlineTypographyColor.style}}>
                        {t(subheadline)}
                    </Typography>
                </Box>
                }
                {link &&
                <Box className={classes.buttonContainer} style={{justifyContent: subheadlineJustifyContent, textAlign: subheadlineTextAlign}}>
                    <SmartButton configuration={buttonConfiguration}/>
                </Box>
                }
            </Box>
        </StyledBox>
    );
}
